import { Box, Stack, Typography } from '@mui/material';
import type { GridSortModel } from '@mui/x-data-grid';
import { toast, useIsMobile } from '@side/pantry';
import { format } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@/components/DataGrid';
import { ExportToCSVButton } from '../components/ExportToCSVButton';
import {
  EXPORT_PAGE_SIZE,
  fetchAllPagesReportingSummaryPages,
  fetchUsersAndTeamsMaps,
  mapDataGridColumnToValueOrEmpty,
  useFilterColumnsByUserRole,
} from '../helpers';
import { usePerformanceReportContext } from '../PerformanceReport/context/PerformanceReportContext';
import { useAllTimeStartDate } from '../ReportsConfiguration/useReportsConfiguration';
import { buildCsv, downloadCsv } from '../ReportsExport/ReportsExport.helper';
import { TablePagination } from '../TablePagination/TablePagination';
import { useGetTransactions } from './hooks/useGetTransactions';
import {
  buildTransactionsSummaryQuery,
  columns,
  DEFAULT_PAGE_SIZE,
  mapTransactionToRow,
  PAGE_SIZE_OPTIONS,
  TransactionsTableColumns,
  type TransactionsTableRow,
  transactionsDataGroups,
  VISIBILITY_MODEL,
} from './TransactionsTable.helpers';

interface Props {
  viewReportAs: string;
}

export function TransactionsTable({ viewReportAs }: Props) {
  const isMobile = useIsMobile('md');
  const { filters } = usePerformanceReportContext();
  const allTimeStartDate = useAllTimeStartDate();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: TransactionsTableColumns.closeDate, sort: 'desc' },
  ]);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<Partial<Record<keyof TransactionsTableRow, boolean>>>(
      VISIBILITY_MODEL,
    );
  const [isExporting, setIsExporting] = useState(false);

  const {
    rows,
    loading,
    totalCount,
    rowCount,
    paginationModel,
    setPaginationModel,
  } = useGetTransactions({ sortModel, viewReportAs });

  const rowCountRef = useRef(0);
  if (totalCount !== undefined) rowCountRef.current = totalCount;

  const filteredColumns = useFilterColumnsByUserRole({
    columns,
    dataGroups: transactionsDataGroups,
  });
  const mappedColumns = useMemo(
    () =>
      filteredColumns.map((column) => {
        const mapped = mapDataGridColumnToValueOrEmpty(column);
        if (isMobile && column.field === TransactionsTableColumns.closeDate) {
          return { ...mapped, minWidth: 130 };
        }
        return mapped;
      }),
    [filteredColumns, isMobile],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [setPaginationModel],
  );

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const query = buildTransactionsSummaryQuery(
        filters,
        sortModel,
        viewReportAs,
        {
          offset: 0,
          limit: EXPORT_PAGE_SIZE,
        },
        allTimeStartDate,
      );

      const results = await fetchAllPagesReportingSummaryPages({
        offset: 0,
        query,
      });
      if (results === null) return;

      const { usersById, teamsById } = await fetchUsersAndTeamsMaps({
        userIds: results.flatMap((r) => r.agentList ?? []),
        teamIds: results.flatMap((r) =>
          r.teamIdentityId ? [r.teamIdentityId] : [],
        ),
      });
      const exportRows: TransactionsTableRow[] = results.map((r) =>
        mapTransactionToRow(r, { usersById, teamsById }),
      );
      const csv = buildCsv(exportRows, filteredColumns);
      const filename = `Transactions-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      downloadCsv(csv, filename);
      toast('success', 'Transactions exported successfully', {
        showCloseButton: true,
      });
    } catch {
      toast(
        'error',
        'Export failed. Please try again or contact Side Support for assistance.',
        { showCloseButton: true },
      );
    } finally {
      setIsExporting(false);
    }
  }, [viewReportAs, filters, sortModel, filteredColumns, allTimeStartDate]);

  return (
    <Stack sx={{ gap: 2 }} id="transactions-table">
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'center', md: 'flex-end' },
        }}
      >
        <Stack sx={{ gap: 1 }}>
          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            Transactions
          </Typography>
          {isMobile ? null : (
            <Typography variant="body2" color="text.secondary">
              All transactions matching the current filters
            </Typography>
          )}
        </Stack>
        <ExportToCSVButton
          onClick={() => void handleExportCsv()}
          loading={isExporting}
          disabled={totalCount === 0}
        />
      </Stack>
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          variant="default"
          columns={mappedColumns}
          disableColumnFilter
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          rows={
            loading
              ? Array.from({ length: paginationModel.pageSize }, (_, i) => ({
                  id: String(i),
                  agent: '',
                  team: '',
                }))
              : rows
          }
          rowCount={rowCountRef.current}
          loading={loading}
          paginationMode="server"
          sortingMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          slots={{
            pagination: () => (
              <TablePagination
                variant="performance"
                hidePaginationControls={
                  !totalCount || !rowCount || totalCount <= DEFAULT_PAGE_SIZE
                }
              />
            ),
            noRowsOverlay: () => (
              <Stack
                direction="column"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="body3" color="text.secondary">
                  No transactions match the current filters
                </Typography>
              </Stack>
            ),
          }}
          slotProps={{
            columnsManagement: {
              disableShowHideToggle: false,
              disableResetButton: true,
            },
          }}
          sx={{
            ...(totalCount === 0 && {
              '--DataGrid-overlayHeight': '60px',
            }),
            '& .MuiTablePagination-root': {
              justifyContent: 'flex-start',
              display: 'flex',
            },
            '& .MuiToolbar-root': {
              paddingLeft: 0,
            },
          }}
        />
      </Box>
    </Stack>
  );
}
